import { useMemo, useState } from 'react'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded'
import PublicRoundedIcon from '@mui/icons-material/PublicRounded'
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded'
import { useNls } from '../../nls/nlsProvider'
import type { NlsLocale } from '../../nls/nlsTypes'
import { HELPER_APP_LOCALE_OPTIONS, type HelperAppLocaleOption } from '../../services/helper/languageRegistryService'
import { Button, Pressable } from '../Common'
import { BottomSheet } from '../Common/BottomSheet'
import { groupHelperFirstRunLanguageOptions } from './helperFirstRunLanguagePrompt'

type HelperFirstRunLanguageModalProps = {
  open: boolean
  currentLocale: NlsLocale
  onSelect: (locale: NlsLocale) => Promise<void> | void
  onSkip: () => void
}

const LanguageOptionButton = ({
  option,
  selected,
  busy,
  onSelect,
}: {
  option: HelperAppLocaleOption
  selected: boolean
  busy: boolean
  onSelect: (locale: NlsLocale) => void
}) => {
  const { t } = useNls()
  return (
    <Pressable
      className={`helperFirstRunLanguageOption${selected ? ' isSelected' : ''}`}
      onClick={() => onSelect(option.code)}
      disabled={busy}
      dir={option.rtl ? 'rtl' : 'ltr'}
      aria-pressed={selected}
    >
      <span className="helperFirstRunLanguageOptionIcon" aria-hidden="true">
        {selected ? <CheckCircleRoundedIcon fontSize="inherit" /> : <LanguageRoundedIcon fontSize="inherit" />}
      </span>
      <span className="helperFirstRunLanguageOptionText">
        <strong>{option.nativeLabel}</strong>
        <span>{option.label}</span>
      </span>
      {selected ? <em>{t('NLSHelperFirstRunLanguageCurrent')}</em> : null}
    </Pressable>
  )
}

export const HelperFirstRunLanguageModal = ({
  open,
  currentLocale,
  onSelect,
  onSkip,
}: HelperFirstRunLanguageModalProps) => {
  const { t } = useNls()
  const [busyLocale, setBusyLocale] = useState<NlsLocale | null>(null)
  const [error, setError] = useState<string | null>(null)
  const groupedOptions = useMemo(() => groupHelperFirstRunLanguageOptions(HELPER_APP_LOCALE_OPTIONS), [])

  const handleSelect = async (locale: NlsLocale) => {
    if (busyLocale) return
    setBusyLocale(locale)
    setError(null)
    try {
      await onSelect(locale)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('NLSHelperFirstRunLanguageError'))
    } finally {
      setBusyLocale(null)
    }
  }

  const renderGroup = (label: string, options: HelperAppLocaleOption[], Icon: typeof LanguageRoundedIcon) => (
    <section className="helperFirstRunLanguageGroup">
      <header>
        <Icon fontSize="small" />
        <span>{label}</span>
      </header>
      <div className="helperFirstRunLanguageGrid">
        {options.map((option) => (
          <LanguageOptionButton
            key={option.code}
            option={option}
            selected={option.code === currentLocale}
            busy={Boolean(busyLocale)}
            onSelect={(locale) => void handleSelect(locale)}
          />
        ))}
      </div>
    </section>
  )

  return (
    <BottomSheet
      isOpen={open}
      onClose={onSkip}
      title={t('NLSHelperFirstRunLanguageTitle')}
      maxHeightSvh={92}
      dismissible={false}
      className="helperFirstRunLanguageSheet"
    >
      <div className="helperFirstRunLanguageModal">
        <div className="helperFirstRunLanguageIntro">
          <span aria-hidden="true"><TranslateRoundedIcon fontSize="small" /></span>
          <p>{t('NLSHelperFirstRunLanguageBody')}</p>
        </div>
        {error ? <p className="helperFirstRunLanguageError" role="alert">{error}</p> : null}
        {renderGroup(t('NLSHelperFirstRunLanguageIndian'), groupedOptions.indian, LanguageRoundedIcon)}
        {renderGroup(t('NLSHelperFirstRunLanguageInternational'), groupedOptions.international, PublicRoundedIcon)}
        <Button
          variant="tertiary"
          size="md"
          fullWidth
          icon={<CloseRoundedIcon fontSize="small" />}
          onClick={onSkip}
          disabled={Boolean(busyLocale)}
        >
          {busyLocale ? t('NLSHelperFirstRunLanguageSaving') : t('NLSHelperFirstRunLanguageSkip')}
        </Button>
      </div>
    </BottomSheet>
  )
}
